import { useState, useEffect, useCallback, useRef } from 'react';
import { executeQuery } from '../services/salesforce/client';
import { getCached, setCache, invalidateCache } from '../services/cache';
import type { SalesforceRecord } from '../types/salesforce';

interface UseSalesforceDataResult<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSalesforceData<T = SalesforceRecord>(
  soql: string | null
): UseSalesforceDataResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [forceRefresh, setForceRefresh] = useState(0);

  const refetch = useCallback(() => {
    if (soql) invalidateCache(soql);
    setForceRefresh((t) => t + 1);
  }, [soql]);

  useEffect(() => {
    if (!soql) {
      setData([]);
      setLoading(false);
      return;
    }

    // Check cache first
    const cached = getCached<T[]>(soql);
    if (cached) {
      setData(cached);
      setLoading(false);
      setError(null);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const records = await executeQuery<T>(soql!, controller.signal);
        if (!cancelled) {
          setData(records);
          setCache(soql!, records);
        }
      } catch (err: unknown) {
        if (!cancelled && !(err instanceof DOMException && err.name === 'AbortError')) {
          setError(err instanceof Error ? err.message : 'Failed to fetch data');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [soql, forceRefresh]);

  return { data, loading, error, refetch };
}
